import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { BookOpen, Feather } from "lucide-react";
import api, { formatApiError } from "../api";
import { useAuth } from "../auth";
import { Logo } from "../components/Logo";

/*
 * CHOIX D'ESPACE — page affichée juste après login/register quand `chosen_space` est vide.
 * L'utilisateur peut aussi y revenir à tout moment via le lien "Changer d'espace".
 */
export default function ChoixEspace() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [choosing, setChoosing] = useState("");
  const [err, setErr] = useState("");

  const choose = async (space) => {
    setChoosing(space);
    setErr("");
    try {
      await api.patch("/auth/me/chosen-space", { chosen_space: space });
      await refresh();
      nav(space === "carnet" ? "/carnet/identite" : "/succession", { replace: true });
    } catch (e) {
      setErr(formatApiError(e));
      setChoosing("");
    }
  };

  return (
    <div style={{ minHeight: "100vh", background: "var(--auprea-cream)", display: "flex", flexDirection: "column" }} data-testid="choix-espace-page">
      <header style={{ padding: "20px 24px", borderBottom: "1px solid rgba(21,41,79,0.08)", textAlign: "center" }}>
        <Logo size={26} />
      </header>
      <main style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: "40px 20px" }}>
        <div style={{ maxWidth: 960, width: "100%" }}>
          <div style={{ textAlign: "center", marginBottom: 40 }}>
            <div style={{ color: "var(--auprea-gold)", fontSize: 11, letterSpacing: "0.25em", textTransform: "uppercase", fontWeight: 600, marginBottom: 12 }}>Bienvenue{user?.prenom ? ", " + user.prenom : ""}</div>
            <h1 style={{ fontFamily: '"Spectral", Georgia, serif', color: "var(--auprea-navy)", fontSize: "clamp(28px, 4vw, 40px)", fontWeight: 500, margin: 0, lineHeight: 1.2 }}>
              Par où souhaitez-vous commencer ?
            </h1>
            <p style={{ color: "rgba(35,38,46,0.7)", fontSize: 16, marginTop: 14, maxWidth: 620, margin: "14px auto 0", lineHeight: 1.6 }}>
              Vous pouvez changer d'espace à tout moment depuis votre menu. Aucun choix n'est définitif.
            </p>
          </div>

          {err && (
            <div style={{ background: "rgba(181,58,58,0.08)", border: "1px solid rgba(181,58,58,0.3)", color: "#b53a3a", padding: "10px 16px", borderRadius: 4, marginBottom: 20, fontSize: 14, textAlign: "center" }} data-testid="choix-error">
              {err}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Carnet */}
            <button
              onClick={() => choose("carnet")}
              disabled={!!choosing}
              data-testid="choose-carnet"
              style={{
                textAlign: "left", padding: "32px 28px", background: "#fff",
                border: "1px solid rgba(21,41,79,0.15)",
                borderRadius: 8, cursor: "pointer",
                transition: "all 0.2s ease",
                opacity: choosing && choosing !== "carnet" ? 0.5 : 1,
              }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = "var(--auprea-navy)"; e.currentTarget.style.transform = "translateY(-2px)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = "rgba(21,41,79,0.15)"; e.currentTarget.style.transform = "translateY(0)"; }}
            >
              <div style={{ width: 48, height: 48, background: "rgba(21,41,79,0.08)", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 20 }}>
                <BookOpen size={24} color="var(--auprea-navy)" />
              </div>
              <h2 style={{ fontFamily: '"Spectral", Georgia, serif', fontSize: 24, color: "var(--auprea-navy)", margin: "0 0 10px 0", fontWeight: 500 }}>
                Créer mon carnet
              </h2>
              <p style={{ color: "rgba(35,38,46,0.72)", fontSize: 14, lineHeight: 1.6, margin: "0 0 16px 0" }}>
                Le <strong>Carnet d'Inventaire Patrimonial</strong>. Centralisez vos documents, exprimez vos volontés
                et identifiez les personnes de confiance pour anticiper sereinement votre succession.
              </p>
              <div style={{ display: "inline-block", color: "var(--auprea-gold)", fontSize: 13, fontWeight: 600 }}>
                {choosing === "carnet" ? "Redirection…" : "Commencer mon carnet →"}
              </div>
            </button>

            {/* Succession */}
            <button
              onClick={() => choose("succession")}
              disabled={!!choosing}
              data-testid="choose-succession"
              style={{
                textAlign: "left", padding: "32px 28px",
                background: "linear-gradient(135deg, #123B36 0%, #1F5E56 100%)",
                border: "1px solid #123B36", color: "#EFF1EC",
                borderRadius: 8, cursor: "pointer",
                transition: "all 0.2s ease",
                opacity: choosing && choosing !== "succession" ? 0.5 : 1,
              }}
              onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 8px 24px rgba(31,94,86,0.35)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "none"; }}
            >
              <div style={{ width: 48, height: 48, background: "rgba(173,124,51,0.2)", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 20 }}>
                <Feather size={24} color="#AD7C33" />
              </div>
              <h2 style={{ fontFamily: '"Spectral", Georgia, serif', fontSize: 24, color: "#EFF1EC", margin: "0 0 10px 0", fontWeight: 500 }}>
                Ouvrir une succession
              </h2>
              <p style={{ color: "rgba(239,241,236,0.85)", fontSize: 14, lineHeight: 1.6, margin: "0 0 16px 0" }}>
                Un proche est décédé. En 4 étapes guidées, obtenez votre <strong style={{ color: "#AD7C33" }}>diagnostic gratuit</strong> puis
                votre <strong style={{ color: "#AD7C33" }}>feuille de route personnalisée</strong> pour clôturer sereinement la succession.
              </p>
              <div style={{ display: "inline-block", color: "#AD7C33", fontSize: 13, fontWeight: 600 }}>
                {choosing === "succession" ? "Redirection…" : "Commencer mon diagnostic →"}
              </div>
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}
